/**
 * FieldSourceToggle Component
 *
 * Wraps any input field to allow switching between:
 * - Fixed value: User enters value directly
 * - Mapped value: Value comes from upstream node output
 *
 * Usage:
 * <FieldSourceToggle
 *   fieldKey="emotion_trend"
 *   label="情绪趋势"
 *   availableSources={sources}
 *   mapping={currentMapping}
 *   onMappingChange={handleMappingChange}
 *   onMappingRemove={handleMappingRemove}
 * >
 *   <Select value={value} onChange={onChange} options={options} />
 * </FieldSourceToggle>
 */

import React, { useState, useEffect } from 'react';
import { Link2, Unlink, ChevronDown } from 'lucide-react';
import type {
  FieldType,
  InputFieldMapping,
  ObjectKeyInfo,
  AvailableSource,
  ContextVariable,
  StartVariable,
  CustomInputField,
} from '@/types/workflowFields';
import { getFieldTypeColor, getFieldTypeLabel, toConfigMappingKey, CONFIG_MAPPING_PREFIX } from '@/types/workflowFields';

// Use CONFIG_MAPPING_PREFIX from workflowFields.ts for consistency

interface FieldSourceToggleProps {
  /** Field key for this input */
  fieldKey: string;
  /** Display label (can be string or React element) */
  label: React.ReactNode;
  /** Optional description */
  description?: string;
  /** Whether field is required */
  required?: boolean;
  /** Available source nodes and their outputs */
  availableSources: AvailableSource[];
  /** Available context variables (ctx.xxx) */
  contextVariables?: ContextVariable[];
  /** Available start node variables (var.xxx) */
  startVariables?: StartVariable[];
  /** Current node's custom input fields */
  customInputs?: CustomInputField[];
  /** Current mapping for this field (if any) */
  mapping?: InputFieldMapping;
  /** Callback when mapping is set/changed */
  onMappingChange: (sourceNodeKey: string, sourceOutputKey: string, keyPath?: string) => void;
  /** Callback when key path is updated */
  onKeyPathChange?: (keyPath: string | undefined) => void;
  /** Callback when mapping is removed (switch to fixed mode) */
  onMappingRemove: () => void;
  /** Whether the control is disabled */
  disabled?: boolean;
  /** The fixed-value input control */
  children: React.ReactNode;
  /** Optional: filter sources by compatible type */
  compatibleTypes?: FieldType[];
}

export const FieldSourceToggle: React.FC<FieldSourceToggleProps> = ({
  fieldKey,
  label,
  description,
  required = false,
  availableSources,
  contextVariables = [],
  startVariables = [],
  customInputs = [],
  mapping,
  onMappingChange,
  onKeyPathChange,
  onMappingRemove,
  disabled = false,
  children,
  compatibleTypes,
}) => {
  // Detect if this is a config field by checking the prefix
  const isConfigField = fieldKey.startsWith(CONFIG_MAPPING_PREFIX);

  // FAIL FAST: Config fields (with __config__ prefix) can only reference internal sources
  // They CANNOT directly reference upstream node outputs
  if (isConfigField && mapping) {
    const validSources = ['input', 'ctx', 'var'];
    if (!validSources.includes(mapping.source_node_key)) {
      throw new Error(
        `[FieldSourceToggle] Config field "${fieldKey}" cannot reference upstream node "${mapping.source_node_key}". ` +
        `Config fields can only reference: ${validSources.join(', ')}. ` +
        `To use upstream values, first define them as input fields, then reference via 'input' source.`
      );
    }
  }

  // Mode: 'fixed' or 'mapped'
  const [mode, setMode] = useState<'fixed' | 'mapped'>(mapping ? 'mapped' : 'fixed');
  const [showSourceDropdown, setShowSourceDropdown] = useState(false);
  const [localKeyPath, setLocalKeyPath] = useState(mapping?.source_key_path || '');

  // Sync mode and key path when mapping changes externally
  useEffect(() => {
    setMode(mapping ? 'mapped' : 'fixed');
    setLocalKeyPath(mapping?.source_key_path || '');
  }, [mapping, mapping?.source_key_path]);

  // Filter sources by compatible types if specified
  const filteredSources = compatibleTypes
    ? availableSources.map((source) => ({
        ...source,
        outputs: source.outputs.filter((o) => compatibleTypes.includes(o.type)),
      })).filter((source) => source.outputs.length > 0)
    : availableSources;

  // Get display text for current mapping
  const getMappingDisplayText = () => {
    if (!mapping) return '';

    // Check if it's a context variable (ctx::xxx)
    if (mapping.source_node_key === 'ctx') {
      const ctxVar = contextVariables.find((v) => v.key === mapping.source_output_key);
      return `Context → ${ctxVar?.label || mapping.source_output_key}`;
    }

    // Check if it's a start variable (var::xxx)
    if (mapping.source_node_key === 'var') {
      return `Variable → ${mapping.source_output_key}`;
    }

    // Check if it's a custom input (input::xxx)
    if (mapping.source_node_key === 'input') {
      const inputField = customInputs.find((i) => i.key === mapping.source_output_key);
      return `输入 → ${inputField?.label || mapping.source_output_key}`;
    }

    // Otherwise it's an upstream node output
    const source = availableSources.find((s) => s.node_key === mapping.source_node_key);
    if (!source) return `${mapping.source_node_key}.${mapping.source_output_key}`;
    const output = source.outputs.find((o) => o.key === mapping.source_output_key);
    return `${source.node_label} → ${output?.label || mapping.source_output_key}`;
  };

  // Handle mode toggle
  const handleToggleMode = () => {
    if (mode === 'fixed') {
      // Switch to mapped mode - show dropdown
      setMode('mapped');
      setShowSourceDropdown(true);
    } else {
      // Switch to fixed mode - remove mapping
      setMode('fixed');
      onMappingRemove();
      setShowSourceDropdown(false);
    }
  };

  // Handle source selection
  const handleSourceSelect = (combinedValue: string) => {
    if (!combinedValue) return;
    const [nodeKey, outputKey] = combinedValue.split('::');
    if (nodeKey && outputKey) {
      // Clear key path when changing source
      setLocalKeyPath('');
      onMappingChange(nodeKey, outputKey, undefined);
      setShowSourceDropdown(false);
    }
  };

  // Get the type and object_keys of the currently selected output
  const getSelectedOutputInfo = (): { type?: FieldType; object_keys?: ObjectKeyInfo[] } => {
    if (!mapping) return {};

    // Check if it's a custom input (input::xxx)
    if (mapping.source_node_key === 'input') {
      const inputField = customInputs.find((i) => i.key === mapping.source_output_key);
      return { type: inputField?.type, object_keys: inputField?.object_keys };
    }

    // Otherwise it's an upstream node output
    const source = availableSources.find((s) => s.node_key === mapping.source_node_key);
    if (!source) return {};
    const output = source.outputs.find((o) => o.key === mapping.source_output_key);
    return { type: output?.type, object_keys: output?.object_keys };
  };

  const { type: selectedOutputType, object_keys: objectKeys } = getSelectedOutputInfo();
  const isObjectType = selectedOutputType === 'object';

  const handleKeyPathBlur = () => {
    const trimmed = localKeyPath.trim();
    if (onKeyPathChange) {
      onKeyPathChange(trimmed || undefined);
    }
  };

  const handleKeyPathKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleKeyPathBlur();
    }
  };

  // Check if there are any available sources (nodes, ctx, var, or custom inputs)
  // When isConfigField=true, upstream sources are NOT allowed
  const hasUpstreamSources = !isConfigField && filteredSources.length > 0 && filteredSources.some((s) => s.outputs.length > 0);
  const hasContextVars = contextVariables.length > 0;
  const hasStartVars = startVariables.length > 0;
  const hasCustomInputs = customInputs.length > 0;
  const hasAvailableSources = hasUpstreamSources || hasContextVars || hasStartVars || hasCustomInputs;

  return (
    <div className="space-y-1.5">
      {/* Label row with toggle button */}
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>

        {hasAvailableSources && (
          <button
            type="button"
            onClick={handleToggleMode}
            disabled={disabled}
            className={`
              flex items-center gap-1 px-2 py-1 text-xs rounded-md transition-colors
              ${mode === 'mapped'
                ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }
              ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
            `}
            title={mode === 'mapped' ? '切换为固定值' : '从上游节点获取'}
          >
            {mode === 'mapped' ? (
              <>
                <Link2 className="w-3 h-3" />
                <span>已映射</span>
              </>
            ) : (
              <>
                <Unlink className="w-3 h-3" />
                <span>固定值</span>
              </>
            )}
          </button>
        )}
      </div>

      {/* Content based on mode */}
      {mode === 'fixed' ? (
        // Fixed value mode - show original input
        <div>{children}</div>
      ) : (
        // Mapped mode - show source selector
        <div className="relative">
          {mapping ? (
            // Show current mapping
            <button
              type="button"
              onClick={() => setShowSourceDropdown(!showSourceDropdown)}
              disabled={disabled}
              className="w-full flex items-center justify-between px-3 py-2 text-sm border border-blue-300 rounded-md bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
            >
              <span className="flex items-center gap-2">
                <Link2 className="w-4 h-4" />
                {getMappingDisplayText()}
              </span>
              <ChevronDown className="w-4 h-4" />
            </button>
          ) : (
            // Show dropdown to select source
            <button
              type="button"
              onClick={() => setShowSourceDropdown(!showSourceDropdown)}
              disabled={disabled}
              className="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-300 rounded-md bg-white text-gray-500 hover:bg-gray-50 transition-colors"
            >
              <span>选择数据来源...</span>
              <ChevronDown className="w-4 h-4" />
            </button>
          )}

          {/* Source dropdown */}
          {showSourceDropdown && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg max-h-64 overflow-auto">
              {/* Custom Input Variables (当前节点定义的输入) */}
              {hasCustomInputs && (
                <div>
                  <div className="px-3 py-2 text-xs font-semibold text-blue-600 bg-blue-50 sticky top-0">
                    📥 当前输入变量
                  </div>
                  {customInputs.map((input) => {
                    const isSelected = mapping?.source_node_key === 'input' && mapping?.source_output_key === input.key;
                    return (
                      <button
                        key={`input::${input.key}`}
                        type="button"
                        onClick={() => handleSourceSelect(`input::${input.key}`)}
                        className={`w-full flex items-center gap-2 px-4 py-2 text-sm text-left hover:bg-blue-50 ${isSelected ? 'bg-blue-100 text-blue-700' : 'text-gray-700'}`}
                      >
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: getFieldTypeColor(input.type) }} />
                        {input.label || input.key}
                        <span className="text-xs text-gray-400 ml-auto">{`{{${input.key}}}`}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Context Variables */}
              {hasContextVars && (
                <div>
                  <div className="px-3 py-2 text-xs font-semibold text-cyan-600 bg-cyan-50 sticky top-0">
                    🌐 Context 变量
                  </div>
                  {contextVariables.map((ctxVar) => {
                    const isSelected = mapping?.source_node_key === 'ctx' && mapping?.source_output_key === ctxVar.key;
                    return (
                      <button
                        key={`ctx::${ctxVar.key}`}
                        type="button"
                        onClick={() => handleSourceSelect(`ctx::${ctxVar.key}`)}
                        className={`w-full flex items-center gap-2 px-4 py-2 text-sm text-left hover:bg-blue-50 ${isSelected ? 'bg-blue-100 text-blue-700' : 'text-gray-700'}`}
                      >
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: getFieldTypeColor(ctxVar.type) }} />
                        {ctxVar.label}
                        <span className="text-xs text-gray-400 ml-auto">{`{{ctx.${ctxVar.key}}}`}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Start Variables */}
              {hasStartVars && (
                <div>
                  <div className="px-3 py-2 text-xs font-semibold text-purple-600 bg-purple-50 sticky top-0">
                    📝 自定义变量 (var)
                  </div>
                  {startVariables.map((startVar) => {
                    const isSelected = mapping?.source_node_key === 'var' && mapping?.source_output_key === startVar.key;
                    return (
                      <button
                        key={`var::${startVar.key}`}
                        type="button"
                        onClick={() => handleSourceSelect(`var::${startVar.key}`)}
                        className={`w-full flex items-center gap-2 px-4 py-2 text-sm text-left hover:bg-blue-50 ${isSelected ? 'bg-blue-100 text-blue-700' : 'text-gray-700'}`}
                      >
                        <span className="w-2 h-2 rounded-full bg-purple-400" />
                        {startVar.key}
                        <span className="text-xs text-gray-400 ml-auto">{`{{var.${startVar.key}}}`}</span>
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Upstream Node Outputs - NOT shown for config fields (__config__ prefix) */}
              {!isConfigField && filteredSources.map((source) => (
                <div key={source.node_key}>
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 bg-gray-50 sticky top-0">
                    📦 {source.node_label}
                  </div>
                  {source.outputs.map((output) => {
                    const isSelected = mapping?.source_node_key === source.node_key && mapping?.source_output_key === output.key;
                    return (
                      <button
                        key={`${source.node_key}::${output.key}`}
                        type="button"
                        onClick={() => handleSourceSelect(`${source.node_key}::${output.key}`)}
                        className={`w-full flex items-center gap-2 px-4 py-2 text-sm text-left hover:bg-blue-50 ${isSelected ? 'bg-blue-100 text-blue-700' : 'text-gray-700'}`}
                      >
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: getFieldTypeColor(output.type) }} />
                        {output.label || output.key}
                        {output.type === 'object' && <span className="text-xs text-gray-400 ml-1">[可选key]</span>}
                      </button>
                    );
                  })}
                </div>
              ))}

              {!hasAvailableSources && (
                <div className="px-3 py-4 text-sm text-gray-400 text-center">
                  没有可用的数据源
                </div>
              )}
            </div>
          )}

          {/* Key Path Input - shown only for object type outputs */}
          {isObjectType && mapping && onKeyPathChange && (
            <div className="flex items-center gap-2 mt-2">
              <span className="text-xs text-gray-500 flex-shrink-0">提取 key:</span>
              {objectKeys && objectKeys.length > 0 ? (
                // Has schema info: show dropdown selector
                <select
                  value={localKeyPath}
                  onChange={(e) => {
                    const value = e.target.value;
                    setLocalKeyPath(value);
                    onKeyPathChange(value || undefined);
                  }}
                  disabled={disabled}
                  className="flex-1 h-7 px-2 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  <option value="">（整个对象）</option>
                  {objectKeys.map((k) => (
                    <option key={k.key} value={k.key}>
                      {k.label || k.key} ({getFieldTypeLabel(k.type)})
                    </option>
                  ))}
                </select>
              ) : (
                // No schema info: show text input as fallback
                <input
                  type="text"
                  value={localKeyPath}
                  onChange={(e) => setLocalKeyPath(e.target.value)}
                  onBlur={handleKeyPathBlur}
                  onKeyDown={handleKeyPathKeyDown}
                  placeholder="如: user_id, preferences.theme"
                  disabled={disabled}
                  className="flex-1 h-7 px-2 text-xs border border-gray-300 rounded bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-100 placeholder:text-gray-400"
                />
              )}
              {localKeyPath && (
                <span className="text-xs text-green-600 flex-shrink-0">
                  → .{localKeyPath}
                </span>
              )}
            </div>
          )}
        </div>
      )}

      {/* Description */}
      {description && mode === 'fixed' && (
        <p className="text-xs text-gray-500">{description}</p>
      )}
    </div>
  );
};

/**
 * Hook to manage field source mappings for a node's "Config Options" section.
 *
 * KEY DESIGN: Config mappings use a prefix (__config__) to separate from Input Field mappings.
 *
 * Scope Separation:
 * - Input Fields Section: key = 'title', source = 'lucky_charm' (upstream node)
 * - Config Options Section: key = '__config__title', source = 'input' (custom input reference)
 *
 * This ensures the two sections' mappings never collide even if they reference
 * the same logical field name.
 */
export function useFieldSourceMappings(
  inputMappings: InputFieldMapping[],
  updateMapping: (inputFieldKey: string, sourceNodeKey: string, sourceOutputKey: string, keyPath?: string) => void,
  updateKeyPath: (inputFieldKey: string, keyPath: string | undefined) => void,
  removeMapping: (inputFieldKey: string) => void
) {
  // Get mapping for a config field (uses prefixed key internally)
  const getMappingForField = (fieldKey: string): InputFieldMapping | undefined => {
    const configKey = toConfigMappingKey(fieldKey);
    const mapping = inputMappings.find((m) => m.input_field_key === configKey);
    return mapping;
  };

  // Create handler for a specific config field
  // Internally uses prefixed key, but exposes original fieldKey to caller
  const createFieldHandlers = (fieldKey: string) => {
    const configKey = toConfigMappingKey(fieldKey);
    return {
      mapping: getMappingForField(fieldKey),
      onMappingChange: (sourceNodeKey: string, sourceOutputKey: string, keyPath?: string) =>
        updateMapping(configKey, sourceNodeKey, sourceOutputKey, keyPath),
      onKeyPathChange: (keyPath: string | undefined) => updateKeyPath(configKey, keyPath),
      onMappingRemove: () => removeMapping(configKey),
    };
  };

  return {
    getMappingForField,
    createFieldHandlers,
  };
}

export default FieldSourceToggle;
